import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';

/**
 * ApproveServiceLogModal — the confirmation step at "Approve service log".
 *
 * Until now this was a one-line `confirm()` browser pop-up that just asked
 * "Create invoice for X?" — no opportunity for the manager to set the email
 * recipients, payment terms, or any customer-facing notes before the
 * invoice was minted. The Send modal asked for those details, but by then
 * the bookkeeper, not the manager who did the service, was making the call.
 *
 * This modal moves the decisions to the right person at the right time:
 *   - Recipient email (defaults to customer of record, editable)
 *   - Additional CC emails (comma-separated, stored on the invoice and
 *     pre-fills the Send modal later)
 *   - Payment terms (defaults to the country's setting, override-able)
 *   - Customer-facing notes (optional, appears on the invoice PDF)
 *
 * The values flow through to invoice-manage create_from_service_log which
 * snapshots them on the draft invoice.
 */
export interface ApproveServiceLogModalProps {
  log: {
    id: string;
    customer_name?: string | null;
    customer_email?: string | null;
    total_amount?: number | null;
    service_date?: string | null;
  };
  onClose: () => void;
  onConfirm: (overrides: {
    recipient_email: string | null;
    additional_cc_emails: string | null;
    notes: string | null;
    payment_terms_days: number | null;
  }) => Promise<void>;
}

function ApproveServiceLogModal({ log, onClose, onConfirm }: ApproveServiceLogModalProps) {
  const { supabase } = useAuth();
  const { formatCurrency } = useConfig();

  const [submitting, setSubmitting] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(log.customer_email || '');
  const [additionalCC, setAdditionalCC] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<string>('default');
  const [customPaymentTerms, setCustomPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');

  // Country default — pulled from country_config so the dropdown shows
  // "Country default (X days)" with a real number rather than a vague label.
  const [countryDefaultDays, setCountryDefaultDays] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('country_config')
        .select('payment_terms_days')
        .limit(1)
        .maybeSingle();
      if (!cancelled && data?.payment_terms_days != null) {
        setCountryDefaultDays(Number(data.payment_terms_days));
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  };

  const resolvePaymentTermsDays = (): number | null => {
    if (paymentTerms === 'default') return null; // server falls back to country config
    if (paymentTerms === 'custom') {
      const n = Number(customPaymentTerms);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
    }
    const n = Number(paymentTerms);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  const handleApprove = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        recipient_email: recipientEmail.trim() || null,
        additional_cc_emails: additionalCC.trim() || null,
        notes: notes.trim() || null,
        payment_terms_days: resolvePaymentTermsDays(),
      });
      // Parent closes the modal on success.
    } catch {
      // Parent handles the error toast. Re-enable the button so the user
      // can retry or cancel.
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="m-0">Approve service log</h2>
          <button onClick={onClose} className="modal-close" disabled={submitting}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Summary */}
          <div className="highlight-box highlight-box--primary mb-lg">
            <div className="font-semibold mb-xs">
              {log.customer_name || 'Customer'}
            </div>
            <div style={{ fontSize: 'var(--font-sm)', color: 'var(--color-text-light)' }}>
              <div className="font-medium" style={{ color: 'var(--color-text)' }}>
                Total: {formatCurrency(Number(log.total_amount) || 0)} &middot; Service date: {formatDate(log.service_date)}
              </div>
            </div>
          </div>

          {/* Recipient email */}
          <div className="form-group">
            <label className="form-label">Send invoice to</label>
            <input
              type="email"
              className="form-input"
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
              placeholder="Customer email"
            />
            <div className="form-help">Pre-filled from the customer record. Edit if the customer wants invoices going to a different address.</div>
          </div>

          {/* Additional CCs */}
          <div className="form-group">
            <label className="form-label">Additional CC emails (optional)</label>
            <input
              type="text"
              className="form-input"
              value={additionalCC}
              onChange={e => setAdditionalCC(e.target.value)}
              placeholder="e.g. bookkeeper@example.com, manager@example.com"
            />
            <div className="form-help">Comma-separated. Saved on the invoice and pre-filled when the email is sent.</div>
          </div>

          {/* Payment terms */}
          <div className="form-group">
            <label className="form-label">Payment terms</label>
            <select
              className="form-select"
              value={paymentTerms}
              onChange={e => setPaymentTerms(e.target.value)}
            >
              <option value="default">
                Country default{countryDefaultDays != null ? ` (${countryDefaultDays} days)` : ''}
              </option>
              <option value="0">Due on receipt</option>
              <option value="3">Net 3 days</option>
              <option value="7">Net 7 days</option>
              <option value="15">Net 15 days</option>
              <option value="30">Net 30 days</option>
              <option value="45">Net 45 days</option>
              <option value="60">Net 60 days</option>
              <option value="custom">Custom…</option>
            </select>
            {paymentTerms === 'custom' && (
              <input
                type="number"
                min="0"
                step="1"
                className="form-input mt-sm"
                value={customPaymentTerms}
                onChange={e => setCustomPaymentTerms(e.target.value)}
                placeholder="Number of days"
              />
            )}
          </div>

          {/* Customer-facing notes */}
          <div className="form-group">
            <label className="form-label">Notes on the invoice (optional)</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Service split across two visits — thanks for the referral"
            />
            <div className="form-help">Appears on the invoice PDF the customer sees.</div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleApprove} disabled={submitting}>
            {submitting ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ApproveServiceLogModal;
